import type { Competition, CompetitionHabit } from '@/types/competitions';
import { competitionDayCount, type CompetitionWindow } from './competitionDates';

/**
 * Default dates for a copy (namat#27): the same number of days as the
 * source, starting a week from today. Local midnights, like the date picker;
 * createCompetition turns the end day into its last moment.
 */
export function defaultCopyDates(source: CompetitionWindow, today: Date): CompetitionWindow {
  const days = competitionDayCount(source);
  const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
  const endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + days - 1);
  return { startDate, endDate };
}

export interface CompetitionCopyOptions {
  name: string;
  organizationId: string;
  startDate: Date;
  endDate: Date;
  createdBy: string;
  newHabitId: () => string;
}

export type CompetitionDraft = Omit<Competition, 'id' | 'createdAt' | 'updatedAt' | 'status'>;

/**
 * A new competition built from an existing one (namat#27), e.g. to put a
 * competition into the right organization after it was created in the wrong
 * one.
 *
 * Copied: name (editable), description, and the habits — each with a new id,
 * so the copy shares no habit id with the source. Chosen: target
 * organization and dates. Always a draft (isPublished: false) so nobody sees
 * it before the manager checks it.
 *
 * Never copied: logs, standings and participants. Those live outside the
 * competition doc (competitionsLogs by competitionId; participants are the
 * target organization's beneficiaries), and the fields are whitelisted, so
 * nothing else on the source doc rides along either.
 */
export function buildCompetitionCopy(source: Competition, options: CompetitionCopyOptions): CompetitionDraft {
  const habits = source.habits.map((habit): CompetitionHabit => {
    const copy: CompetitionHabit = { id: options.newHabitId(), name: habit.name, category: habit.category };
    if (habit.description) copy.description = habit.description;
    if (habit.stepsTarget) copy.stepsTarget = habit.stepsTarget;
    return copy;
  });

  const draft: CompetitionDraft = {
    name: options.name.trim() || source.name,
    organizationId: options.organizationId,
    startDate: options.startDate,
    endDate: options.endDate,
    habits,
    createdBy: options.createdBy,
    isPublished: false,
  };
  if (source.description) draft.description = source.description;
  return draft;
}
